import React, { useEffect, useState, useCallback } from "react";
import api from "../helpers/api";
import { motion, AnimatePresence } from "framer-motion";
import { formatCreatedDate, formatCamelCase } from "../constants/utils";
import { Card, CardBody, CardHeader, Row, Col, Button, Spinner, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { WorkOrderStatusBadge } from "../components/Common";
import { useTranslation } from 'react-i18next';

const Schedule = () => {
    const { t } = useTranslation();
    document.title = `${t("shared.schedule", "Schedule")} | ArcMedix`;

    const [scheduleData, setScheduleData] = useState([]);
    const [activeTab, setActiveTab] = useState('PENDING');
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const user = JSON.parse(localStorage.getItem('authUser'));
    const organizationId = user?.activeOrganizationId ?? null;

    const tabs = [
        { id: 'PENDING', label: t("shared.pending", 'Pending'), color: 'warning' },
        { id: 'CANCELLED', label: t("shared.cancelled", 'Cancelled'), color: 'secondary' },
        { id: 'RETRY', label: t("shared.retry", 'Retry'), color: 'info' },
        { id: 'FAILED', label: t("shared.failed", 'Failed'), color: 'danger' },
        { id: 'COMPLETED', label: t("shared.complete", 'Complete'), color: 'success' }
    ];

    const fetchScheduleData = useCallback(async () => {
        if (!organizationId) return;

        setLoading(true);
        try {
            const { data } = await api.get(`scheduler/status/${activeTab}`, {
                params: {
                    organizationId,
                    count
                }
            });
            setScheduleData(data?.response || []);

            if (data?.response?.length < count) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        } catch (error) {
            console.error('Error fetching schedule data:', error);
            setScheduleData([]);
        } finally {
            setLoading(false);
        }
    }, [organizationId, activeTab, count]);

    useEffect(() => {
        fetchScheduleData();
    }, [fetchScheduleData]);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setScheduleData([]);
            setCount(10);
            setHasMore(true);
        }
    };

    const handleLoadMore = () => {
        setCount(prevCount => prevCount + 10);
    };


    const renderScheduleTable = () => {
        return (
            <div
                className="table-responsive"
                style={{
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    maxHeight: "calc(100vh - 300px)",
                    overflowY: "auto",
                }}
            >
                <table
                    className="table table-hover mb-0"
                    style={{
                        borderSpacing: "0.1rem",
                        borderCollapse: "separate",
                    }}
                >
                    <thead
                        style={{
                            position: "sticky",
                            top: 0,
                            background: "white",
                            zIndex: 1,
                        }}
                    >
                        <tr>
                            <th>#</th>
                            <th>{t("shared.method_name", "Method Name")}</th>
                            <th>{t("shared.status", "Status")}</th>
                            <th>{t("shared.retry_count", "Retry Count")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <AnimatePresence>
                            {scheduleData?.map((schedule, ind) => (
                                <motion.tr
                                    key={schedule?.id || ind}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <th scope="row">{ind + 1}</th>
                                    <td className="fw-medium">
                                        {schedule?.methodName ? formatCamelCase(schedule.methodName) : t("shared.n_a", 'N/A')}
                                    </td>
                                    <td><WorkOrderStatusBadge status={schedule?.status} /></td>
                                    <td>
                                        <Badge color={schedule?.retryCount > 0 ? 'warning' : 'secondary'} pill>
                                            {schedule?.retryCount ?? 0}
                                        </Badge>
                                    </td>
                                </motion.tr>
                            ))}
                        </AnimatePresence>
                    </tbody>
                </table>
                {loading && (
                    <div className="text-center py-3">
                        <Spinner color="primary" />
                    </div>
                )}
                {!loading && scheduleData.length === 0 && (
                    <div className="text-center py-4">
                        <p className="text-muted">{t("shared.no_schedule_data_found", "No schedule data found")}</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="page-content mt-4">
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="card-title text-success mb-0">{t("shared.schedule", "Schedule")}</h3>
                </CardHeader>

                {/* Tabs */}
                <Nav pills className="justify-content-start px-3 pt-3 gap-2">
                    {tabs.map((tab) => (
                        <NavItem key={tab.id}>
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <NavLink
                                    className={activeTab === tab.id ? "active" : ""}
                                    onClick={() => toggleTab(tab.id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    {tab.label}
                                </NavLink>
                            </motion.div>
                        </NavItem>
                    ))}
                </Nav>

                <TabContent activeTab={activeTab}>
                    {tabs.map((tab) => (
                        <TabPane key={tab.id} tabId={tab.id}>
                            <CardBody>
                                {renderScheduleTable()}

                                {/* Load More Button */}
                                {hasMore && scheduleData.length > 0 && !loading && (
                                    <div className="text-center mt-3">
                                        <Button
                                            color="primary"
                                            onClick={handleLoadMore}
                                            disabled={loading}
                                        >
                                            {loading ? t("shared.loading", "Loading...") : t("shared.load", "Load More")}
                                        </Button>
                                    </div>
                                )}
                                {!hasMore && scheduleData.length > 0 && !loading && (
                                    <div className="text-center py-2 mt-3 text-muted">
                                        {t("shared.no_more_schedule_data_to_load", "No more schedule data to load")}
                                    </div>
                                )}
                            </CardBody>
                        </TabPane>
                    ))}
                </TabContent>
            </Card>
        </div>
    );
};

export default Schedule;
